import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, RotateCw, XCircle } from "lucide-react";
import type { GoogleSyncStatus } from "@axorial/shared";
import { api } from "@/lib/api";

interface MyGoogleSyncState {
  userId: string;
  enabled: boolean;
  calendarId: string;
  lastSyncAt: number | null;
  lastSyncStatus: GoogleSyncStatus | null;
  lastSyncError: string | null;
  lastSyncCount: number;
  neverSynced: boolean;
}

function relativeTime(unixSeconds: number | null): string {
  if (!unixSeconds) return "—";
  const diffSec = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diffSec < 60) return "hace segundos";
  if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
  return `hace ${Math.floor(diffSec / 86400)} d`;
}

/**
 * Banner compacto que muestra el estado del sync de Google Calendar del
 * propio usuario y permite encender/apagar + forzar sync. Pensado para
 * embedirse arriba del calendario.
 */
export function GoogleCalendarSyncCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MyGoogleSyncState>({
    queryKey: ["me", "google-calendar-sync"],
    queryFn: () => api.get("/me/google-calendar-sync"),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.patch("/me/google-calendar-sync", { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "google-calendar-sync"] }),
  });
  const force = useMutation({
    mutationFn: () => api.post("/me/google-calendar-sync/force", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "google-calendar-sync"] });
      qc.invalidateQueries({ queryKey: ["calendar", "me"] });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="card-elevated p-3 text-xs text-base-content/55">
        Cargando estado de Google Calendar…
      </div>
    );
  }

  const statusBadge = (() => {
    if (data.neverSynced || !data.lastSyncStatus) {
      return <span className="badge badge-sm badge-ghost gap-1">Aún no sincronizado</span>;
    }
    if (data.lastSyncStatus === "ok") {
      return (
        <span className="badge badge-sm badge-success gap-1">
          <CheckCircle2 className="size-3" />
          OK · {relativeTime(data.lastSyncAt)}
        </span>
      );
    }
    if (data.lastSyncStatus === "auth_error") {
      return (
        <span className="badge badge-sm badge-error gap-1" title={data.lastSyncError ?? undefined}>
          <XCircle className="size-3" />
          Sin permiso de Google
        </span>
      );
    }
    return (
      <span className="badge badge-sm badge-warning gap-1" title={data.lastSyncError ?? undefined}>
        <AlertTriangle className="size-3" />
        {data.lastSyncStatus}
      </span>
    );
  })();

  return (
    <div className="card-elevated p-3 flex flex-wrap items-center gap-3 shrink-0">
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">Google Calendar</span>
        {statusBadge}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            className="toggle toggle-primary toggle-sm"
            checked={data.enabled}
            onChange={(e) => toggle.mutate(e.target.checked)}
            disabled={toggle.isPending}
          />
          {data.enabled ? "Sincronizando" : "Apagado"}
        </label>
        <button
          type="button"
          onClick={() => force.mutate()}
          disabled={force.isPending || !data.enabled}
          className="btn btn-ghost btn-xs"
          title="Sincronizar ahora"
        >
          {force.isPending ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <RotateCw className="size-3" />
          )}
          Sincronizar ahora
        </button>
      </div>
    </div>
  );
}
